import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { Box, CssBaseline, Typography, Grid, Button, Snackbar, Alert } from '@mui/material';
import DesktopNav from '../../components/desktop/DesktopNav';
import { useNavigate, useLocation } from 'react-router-dom';
import useLogout from '../../hooks/useLogout';
import { STATE_MACHINE, useMergeContext } from '../../store/mergeContext';
import { CrashTable } from './subPages/CrashTable';
import { useTheme } from '@emotion/react';
import { MatchTable } from './subPages/MatchTable';
import { MergePage } from './subPages/MergePage';
import { MergeForm } from './subPages/MergeForm';
import { NewApiBaseUrl } from "../../consts/new-api-base-url";
import useHttpApiNew from "../../hooks/useHttpApiNew";
import DesktopToolbar from "../../components/desktop/DesktopToolbar";
import SvgCustomIcon from "../../components/icons/SvgCustomIcon";
import { NAV_LAYOUT_MARGIN_START } from '../../consts/nav-layout-margin';

// eslint-disable-next-line no-restricted-globals
const newGetAllCrashesUrl = new URL(`${NewApiBaseUrl}/crash/all`);

const MERGE_LIST_LIMIT = 100;
const MERGE_LIST_OFFSET = 0;

export const DataMergingAndValidation = ({ position, isMobile }) => {
  const { _ } = useLingui();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const token = location.state?.token || null;
  const init = useMemo(
    () => location.state?.init || {},
    [location.state?.init]
  );
  const theme = useTheme();
  const { request } = useHttpApiNew();
  const [isLoadingCrashes, setIsLoadingCrashes] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState({ open: false, message: '' });

  const {
    setCrashes,
    currentState,
    setMergedCrashes,
    handleBack,
    sourceBasicOrganization,
    daysFilter,
  } = useMergeContext();

  const buildPendingListUrl = useCallback(() => {
    const url = new URL(newGetAllCrashesUrl);
    url.searchParams.set('format', 'list');
    url.searchParams.set('merged', '0');
    url.searchParams.set('limit', String(MERGE_LIST_LIMIT));
    url.searchParams.set('offset', String(MERGE_LIST_OFFSET));
    url.searchParams.set('days', String(daysFilter));
    url.searchParams.set('basicOrganization', String(sourceBasicOrganization));
    return url;
  }, [daysFilter, sourceBasicOrganization]);

  const buildValidatedListUrl = useCallback(() => {
    const url = new URL(newGetAllCrashesUrl);
    url.searchParams.set('format', 'list');
    url.searchParams.set('merged', '1');
    url.searchParams.set('limit', String(MERGE_LIST_LIMIT));
    url.searchParams.set('offset', String(MERGE_LIST_OFFSET));
    url.searchParams.set('days', String(daysFilter));
    return url;
  }, [daysFilter]);

  const getCrashes = useCallback(async (signal) => {
    setIsLoadingCrashes(true);
    try {
      const res = await request(
        buildPendingListUrl(),
        'GET',
        null,
        { Authorization: `Bearer ${token}` },
        { signal },
      );
      if (res?.aborted || res?.responseAnyError) return;
      setCrashes(res.responseData.crashes ?? []);
    } finally {
      if (!signal?.aborted) setIsLoadingCrashes(false);
    }
  }, [token, setCrashes, buildPendingListUrl, request]);

  const getMergedCrashes = useCallback(async (signal) => {
    const res = await request(
      buildValidatedListUrl(),
      'GET',
      null,
      { Authorization: `Bearer ${token}` },
      { signal },
    );
    if (res?.aborted || res?.responseAnyError) return;
    setMergedCrashes(res.responseData.crashes ?? []);
  }, [token, setMergedCrashes, buildValidatedListUrl, request]);

  const handleMergeActionSuccess = useCallback((message) => {
    setSuccessSnackbar({ open: true, message });
  }, []);

  const fetchCrashes = useCallback(async () => {
    await getMergedCrashes();
    await getCrashes();
  }, [getCrashes, getMergedCrashes]);

  // Pending list: refetch when source org or days change (StrictMode-safe via abort).
  useEffect(() => {
    const controller = new AbortController();
    getCrashes(controller.signal);
    return () => controller.abort();
  }, [getCrashes]);

  // Validated list: mount + days change only (not source org).
  useEffect(() => {
    const controller = new AbortController();
    getMergedCrashes(controller.signal);
    return () => controller.abort();
  }, [getMergedCrashes]);

  return (
    <Box component="main">
      <DesktopNav position={position} subPanelElements={[]} />
      <DesktopToolbar />
      <Box>
        <CssBaseline />
        <Box
          mt={2}
          pl={2}
          pr={2}
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          gap={3}
          sx={{
            marginInlineStart: NAV_LAYOUT_MARGIN_START,
            '& .overload-header': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText
            }
          }}>
          
          <Grid sx={{ display: currentState === STATE_MACHINE.CRASH_TABLE ? 'none' : 'block' }}>
            <Button sx={{ background: theme.palette.newColors.pinkGradient, color: 'white', display: 'flex', gap: 1 }} onClick={() => {handleBack(currentState);}} variant="text">
              <SvgCustomIcon name={'arrowBack'} color={theme.palette.newColors.lightGray} size={16} /> {_(msg`Back`)}
            </Button>
          </Grid>
          {currentState === STATE_MACHINE.CRASH_TABLE &&
          <CrashTable isLoadingCrashes={isLoadingCrashes} />
          }

          {currentState === STATE_MACHINE.MATCH_TABLE && <MatchTable />}

          {currentState === STATE_MACHINE.MERGE_PAGE &&
          <MergePage
            fetchCrashes={fetchCrashes}
            onActionSuccess={handleMergeActionSuccess}
          />
          }

          {currentState === STATE_MACHINE.MERGE_FORM && <MergeForm />}
        </Box>
        <Snackbar
          open={successSnackbar.open}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={() => setSuccessSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setSuccessSnackbar((prev) => ({ ...prev, open: false }))}
            sx={{ width: '100%' }}
          >
            {successSnackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>);

};
